/**
 * PermissionPreview — a plain-language "what can this permission set actually
 * see and do?" summary. Rendered inside the Permission Templates manager so a
 * Super Admin can preview a template BEFORE linking it to a user.
 *
 * It reads the SAME catalog constants the matrix editor uses (materials,
 * report rows/levels, permKeyFor) so the preview never drifts from the
 * editor. The check is a direct membership test against the (canonical)
 * permission keys the matrix produces.
 *
 * Beyond the raw grants it also surfaces cross-permission "gotchas" — most
 * importantly the case that caused a real production bug: a user who can SEE a
 * material (and its ledger) but lacks the Vendors / Goldsmiths master view,
 * which leaves the New Entry / Issue party pickers empty.
 */
import React, { useMemo } from 'react';
import { Check, X, Info, AlertTriangle, Eye } from 'lucide-react';
import {
  MATRIX_MATERIALS,
  MATRIX_LEVELS,
  REPORTS_MATRIX,
  REPORTS_LEVELS,
  permKeyFor,
} from '@/components/admin/PermissionMatrixEditor';

// Sidebar / page destinations mirrored from lib/permissions.js so the preview
// matches what the user will actually be able to open.
const NAV_ITEMS = [
  { label: 'Dashboard', always: true },
  { label: 'Order Forms', key: 'orders.view' },
  { label: 'Production Timeline', key: 'orders.view' },
  { label: 'Customers', key: 'orders.view' },
  { label: 'Gold', key: 'materials.gold.see' },
  { label: 'Diamonds', key: 'materials.diamonds.see' },
  { label: 'Colour Stones', key: 'materials.color_stones.see' },
  { label: 'Pearls', key: 'materials.pearls.see' },
  { label: 'Mountings', key: 'materials.mountings.see' },
  { label: 'Jewellery', key: 'jewellery.view' },
  { label: 'Vendors', key: 'vendors.view' },
  { label: 'Goldsmiths', key: 'goldsmiths.view' },
  { label: 'Cutters', key: 'cutters.view' },
  { label: 'CAD Developers', key: 'cad_developers.view' },
  { label: 'Requests', key: 'requests.view' },
  { label: 'Activity Logs', key: 'logs.view' },
];

function YesNo({ ok, children, testId }) {
  return (
    <div
      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px] ${
        ok
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
          : 'border-slate-200 bg-slate-50 text-slate-400'
      }`}
      data-testid={testId}
    >
      {ok ? <Check size={12} className="shrink-0" /> : <X size={12} className="shrink-0" />}
      <span className={ok ? '' : 'line-through decoration-slate-300'}>{children}</span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">{children}</div>
  );
}

export function PermissionPreview({ permissions = [], idPrefix = 'preview' }) {
  const has = useMemo(() => {
    const set = new Set(permissions || []);
    return (k) => set.has(k);
  }, [permissions]);

  const reportViewKeys = REPORTS_MATRIX.map((r) => `reports.${r.key}.view`);
  const anyReport = reportViewKeys.some((k) => has(k));

  // Pages / sidebar the template can open.
  const pages = NAV_ITEMS.map((n) => ({
    label: n.label,
    ok: n.always ? true : has(n.key),
  }));
  const reportsNav = { label: 'Reports', ok: anyReport };

  // Materials the user can see + which levels are granted on each.
  const materialSummary = MATRIX_MATERIALS.map((m) => {
    const levels = MATRIX_LEVELS.filter((lv) => has(permKeyFor(m.key, lv.key)));
    return { ...m, levels, canSee: has(permKeyFor(m.key, 'see')) };
  });
  const seenMaterials = materialSummary.filter((m) => m.canSee);

  // Reports the user can open + value/print for each.
  const reportSummary = REPORTS_MATRIX
    .map((r) => {
      const view = has(`reports.${r.key}.view`);
      const value = has(`reports.${r.key}.value`);
      const print = has(`reports.${r.key}.print`);
      const guardMissing = r.materialGuard && !has(`materials.${r.materialGuard}.see`);
      return { key: r.key, label: r.label, materialGuard: r.materialGuard, view, value, print, guardMissing };
    })
    .filter((r) => r.view);

  // Financial visibility (rate / value across raw material screens).
  const canSeeRawValues =
    has('values.raw_material.view') ||
    REPORTS_MATRIX.some((r) => has(`reports.${r.key}.value`));
  const canSeeJewelleryValues =
    has('values.jewellery_embedded.view') || has('reports.jewellery.value');

  // Order-level actions this permission set can perform. ``orders.edit``
  // implies ``orders.edit_header`` so header editing shows on for either.
  const orderActions = [
    { label: 'Create orders', ok: has('orders.create') },
    { label: 'Edit order forms', ok: has('orders.edit') },
    { label: 'Edit order header', ok: has('orders.edit_header') || has('orders.edit') },
    { label: 'Put on hold', ok: has('orders.hold') },
    { label: 'Mark complete', ok: has('orders.complete') },
    { label: 'Issue to goldsmith', ok: has('orders.issue_to_goldsmith') },
  ];

  // ---- Cross-permission gotchas ------------------------------------------
  const gotchas = [];
  const anyMaterialSee = seenMaterials.length > 0;
  const anyPurchaseOrIssue = MATRIX_MATERIALS.some(
    (m) => has(permKeyFor(m.key, 'record_purchase')) || has(permKeyFor(m.key, 'issue')),
  );
  if (anyMaterialSee && !has('vendors.view')) {
    gotchas.push(
      'Can view material ledgers but NOT the Vendors master — vendor pickers in New Entry / Receive will be empty. Grant "Vendors — View" if they record purchases.',
    );
  }
  if ((anyMaterialSee || anyPurchaseOrIssue) && !has('goldsmiths.view')) {
    gotchas.push(
      'No access to the Goldsmiths master — goldsmith pickers (Issue / Return) will be empty. Grant "Goldsmiths — View" for issuing to a goldsmith.',
    );
  }
  REPORTS_MATRIX.forEach((r) => {
    if (r.materialGuard && has(`reports.${r.key}.view`) && !has(`materials.${r.materialGuard}.see`)) {
      gotchas.push(
        `"${r.label}" report is enabled but will stay hidden — it also needs that material's "See" access.`,
      );
    }
  });
  if (anyMaterialSee && !has('orders.view')) {
    gotchas.push(
      'Can open material ledgers but NOT Order Forms — issuing against an order needs "Orders — View".',
    );
  }

  const nothingGranted = (permissions || []).length === 0;

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-panel`}>
      <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">
        <Eye size={13} className="mt-0.5 shrink-0 text-slate-400" />
        <span>
          A plain-language preview of what a user linked to this template will be able to open and do.
          Dashboard is always available. {(permissions || []).length} permission
          {(permissions || []).length === 1 ? '' : 's'} selected.
        </span>
      </div>

      {nothingGranted && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid={`${idPrefix}-empty`}>
          No permissions selected yet — a user on this template would only see the Dashboard.
        </div>
      )}

      {/* Pages / navigation */}
      <div>
        <SectionTitle>Pages this user can open</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {pages.map((p) => (
            <YesNo key={p.label} ok={p.ok} testId={`${idPrefix}-page-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {p.label}
            </YesNo>
          ))}
          <YesNo ok={reportsNav.ok} testId={`${idPrefix}-page-reports`}>Reports</YesNo>
        </div>
      </div>

      {/* Materials */}
      <div>
        <SectionTitle>Material access</SectionTitle>
        {seenMaterials.length === 0 ? (
          <div className="text-[12px] text-slate-500">No material access — all raw-material screens are hidden.</div>
        ) : (
          <div className="space-y-1.5" data-testid={`${idPrefix}-materials`}>
            {seenMaterials.map((m) => (
              <div key={m.key} className="flex items-center gap-2 flex-wrap" data-testid={`${idPrefix}-material-${m.key}`}>
                <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-medium ${m.tone}`}>{m.label}</span>
                {m.levels.map((lv) => (
                  <span key={lv.key} className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                    <Check size={11} className="text-emerald-600" />{lv.label}
                  </span>
                ))}
              </div>
            ))}
            {materialSummary.filter((m) => !m.canSee).length > 0 && (
              <div className="text-[11px] text-slate-400 pt-0.5">
                Hidden: {materialSummary.filter((m) => !m.canSee).map((m) => m.label).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order actions */}
      <div>
        <SectionTitle>Order actions</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid={`${idPrefix}-order-actions`}>
          {orderActions.map((a) => (
            <YesNo
              key={a.label}
              ok={a.ok}
              testId={`${idPrefix}-order-action-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {a.label}
            </YesNo>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 pt-1">
          "Edit order header" covers Page No, Goldsmith, Customer, Title, Type, Gold Purity &amp; Special
          Instructions (portal rules still apply — e.g. goldsmith locks once material is issued).
        </div>
      </div>

      {/* Reports */}
      <div>
        <SectionTitle>Reports</SectionTitle>
        {reportSummary.length === 0 ? (
          <div className="text-[12px] text-slate-500">No reports visible.</div>
        ) : (
          <div className="space-y-1" data-testid={`${idPrefix}-reports`}>
            {reportSummary.map((r) => (
              <div key={r.key} className="flex items-center gap-2 flex-wrap text-[11.5px]" data-testid={`${idPrefix}-report-${r.key}`}>
                <span className="text-slate-700 font-medium">{r.label}</span>
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Check size={11} className="text-emerald-600" />View
                </span>
                {r.value && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Check size={11} className="text-emerald-600" />See Value
                  </span>
                )}
                {r.print && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Check size={11} className="text-emerald-600" />Print / Export
                  </span>
                )}
                {r.guardMissing && (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <AlertTriangle size={11} />hidden — needs {r.materialGuard} See
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Financial visibility */}
      <div>
        <SectionTitle>Financial visibility (rate / value)</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          <YesNo ok={canSeeRawValues} testId={`${idPrefix}-values-raw`}>Raw-material rate &amp; value</YesNo>
          <YesNo ok={canSeeJewelleryValues} testId={`${idPrefix}-values-jewellery`}>Jewellery embedded-material value</YesNo>
        </div>
      </div>

      {/* Gotchas */}
      {gotchas.length > 0 && (
        <div>
          <SectionTitle>Heads up</SectionTitle>
          <ul className="space-y-1.5" data-testid={`${idPrefix}-gotchas`}>
            {gotchas.map((g, i) => (
              <li
                key={i}
                className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11.5px] text-amber-800"
                data-testid={`${idPrefix}-gotcha-${i}`}
              >
                <Info size={12} className="mt-0.5 shrink-0" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default PermissionPreview;
